import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Database, 
  Upload, 
  Trash2, 
  RefreshCw, 
  Search,
  FileText,
  Download,
  CheckCircle,
  AlertCircle,
  Loader2,
  Brain,
  Sparkles,
  Clock,
  Activity,
  BarChart3,
  FlaskConical,
  TrendingUp,
  Globe,
  ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RAGAdminPage() {
  const { user, token, isAdmin } = useAuth();
  const [ragStats, setRagStats] = useState(null);
  const [finetuneFiles, setFinetuneFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [indexing, setIndexing] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [indexingJobs, setIndexingJobs] = useState([]);
  const [datasetStats, setDatasetStats] = useState(null);
  // Benchmark state
  const [benchmarkRunning, setBenchmarkRunning] = useState(false);
  const [activeBenchmark, setActiveBenchmark] = useState(null);
  const [benchmarkHistory, setBenchmarkHistory] = useState([]);
  const [sampleSize, setSampleSize] = useState(50);
  // Scheduler state
  const [schedulerConfig, setSchedulerConfig] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [schedulerEnabled, setSchedulerEnabled] = useState(false);
  const [scheduleType, setScheduleType] = useState('daily');
  const [bleuThreshold, setBleuThreshold] = useState(30);

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, datasetRes] = await Promise.all([
        fetch(`${API_URL}/api/rag/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/rag/stats/by-dataset`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setRagStats(data.stats);
      }
      
      if (datasetRes.ok) {
        const data = await datasetRes.json();
        setDatasetStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch RAG stats:', error);
    }
  }, [token]);

  const fetchFinetuneFiles = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/finetune/files`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFinetuneFiles(data.files || []);
      }
    } catch (error) {
      console.error('Failed to fetch finetune files:', error);
    }
  }, [token]);

  const fetchIndexingJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/rag/index-jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIndexingJobs(data.jobs || []);
        
        // Check if there's an active job
        const active = data.jobs?.find(j => 
          j.status === 'queued' || 
          j.status === 'parsing' || 
          j.status === 'indexing_en_fr' || 
          j.status === 'indexing_fr_en'
        );
        setActiveJob(active || null);
      }
    } catch (error) {
      console.error('Failed to fetch indexing jobs:', error);
    }
  }, [token]);

  const fetchBenchmarkHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/benchmark/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBenchmarkHistory(data.benchmarks || []);
      }
    } catch (error) {
      console.error('Failed to fetch benchmark history:', error);
    }
  }, [token]);

  const fetchSchedulerConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/scheduler/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSchedulerConfig(data);
        setSchedulerEnabled(data.enabled || false);
        setScheduleType(data.schedule || 'daily');
        setBleuThreshold(data.bleu_threshold || 30);
      }
    } catch (error) {
      console.error('Failed to fetch scheduler config:', error);
    }
  }, [token]);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/scheduler/alerts?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  }, [token]);

  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const response = await fetch(`${API_URL}/api/rag/index-status/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const job = await response.json();
        setActiveJob(job);
        
        // Update stats while indexing
        if (job.current_stats) {
          setRagStats(job.current_stats);
        }
        
        // Check if job is complete
        if (job.status === 'completed') {
          toast.success(`Indexing complete! ${job.en_fr_indexed} EN→FR + ${job.fr_en_indexed} FR→EN vectors`);
          setActiveJob(null);
          setIndexing(false);
          fetchStats();
          fetchIndexingJobs();
        } else if (job.status === 'failed') {
          toast.error(`Indexing failed: ${job.error || job.message}`);
          setActiveJob(null);
          setIndexing(false);
          fetchIndexingJobs();
        }
        
        return job.status;
      }
    } catch (error) {
      console.error('Failed to poll job status:', error);
    }
    return null;
  }, [token, fetchStats, fetchIndexingJobs]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(), 
        fetchFinetuneFiles(), 
        fetchIndexingJobs(), 
        fetchBenchmarkHistory(),
        fetchSchedulerConfig(),
        fetchAlerts()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchFinetuneFiles, fetchIndexingJobs, fetchBenchmarkHistory, fetchSchedulerConfig, fetchAlerts]);

  // Poll for active job status
  useEffect(() => {
    if (!activeJob) return;
    
    const interval = setInterval(async () => {
      const status = await pollJobStatus(activeJob.job_id);
      if (status === 'completed' || status === 'failed') {
        clearInterval(interval);
      }
    }, 3000); // Poll every 3 seconds
    
    return () => clearInterval(interval);
  }, [activeJob, pollJobStatus]);

  // Poll for active benchmark status
  useEffect(() => {
    if (!activeBenchmark) return;
    
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/benchmark/status/${activeBenchmark.benchmark_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setActiveBenchmark(data);
          
          if (data.status === 'completed') {
            toast.success(`Benchmark complete! Avg BLEU: ${data.summary?.avg_bleu_score}`);
            setBenchmarkRunning(false);
            fetchBenchmarkHistory();
            clearInterval(interval);
          } else if (data.status === 'failed') {
            toast.error(`Benchmark failed: ${data.error}`);
            setBenchmarkRunning(false);
            setActiveBenchmark(null);
            clearInterval(interval);
          }
        }
      } catch (error) {
        console.error('Benchmark poll error:', error);
      }
    }, 5000); // Poll every 5 seconds
    
    return () => clearInterval(interval);
  }, [activeBenchmark, token, fetchBenchmarkHistory]);

  const handleIndexTMX = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.tmx')) {
      toast.error('Please select a TMX file');
      return;
    }

    setIndexing(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/rag/index-tmx?dataset_type=training&background=true`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      
      if (response.ok) {
        if (data.background && data.job_id) {
          // Background job started
          toast.info(`Background indexing started for ${data.file_name}`);
          setActiveJob({
            job_id: data.job_id,
            status: 'queued',
            message: 'Job queued...',
            file_name: data.file_name
          });
          fetchIndexingJobs();
        } else {
          // Synchronous processing completed
          toast.success(`Indexed ${data.indexed?.en_to_fr || 0} EN→FR and ${data.indexed?.fr_to_en || 0} FR→EN pairs`);
          setIndexing(false);
          fetchStats();
        }
      } else {
        toast.error(data.detail || 'Failed to index file');
        setIndexing(false);
      }
    } catch (error) {
      toast.error('Failed to index file');
      setIndexing(false);
    } finally {
      e.target.value = '';
    }
  };

  const handlePrepareFinetuning = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.tmx')) {
      toast.error('Please select a TMX file');
      return;
    }

    setPreparing(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/finetune/prepare?dataset_name=eccc_meteo&split_ratio=0.9`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Prepared ${data.stats?.combined?.train || 0} training examples`);
        fetchFinetuneFiles();
      } else {
        toast.error(data.detail || 'Failed to prepare fine-tuning data');
      }
    } catch (error) {
      toast.error('Failed to prepare fine-tuning data');
    } finally {
      setPreparing(false);
      e.target.value = '';
    }
  };

  const handleRunBenchmark = async () => {
    if (benchmarkRunning) return;
    
    // Check if we have validation data
    if (!datasetStats?.totals?.validation || datasetStats.totals.validation === 0) {
      toast.error('No validation data indexed. Please upload val_v4.tmx first.');
      return;
    }

    setBenchmarkRunning(true);

    try {
      const response = await fetch(`${API_URL}/api/benchmark/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_lang: 'en-ca',
          target_lang: 'fr-ca',
          sample_size: sampleSize,
          use_validation_only: true
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.info(`Benchmark started with ${data.sample_size} samples`);
        setActiveBenchmark({
          benchmark_id: data.benchmark_id,
          status: 'running',
          total_samples: data.sample_size
        });
      } else {
        toast.error(data.detail || 'Failed to start benchmark');
        setBenchmarkRunning(false);
      }
    } catch (error) {
      toast.error('Failed to start benchmark');
      setBenchmarkRunning(false);
    }
  };

  const handleClearIndex = async () => {
    if (!window.confirm('Are you sure you want to clear the entire RAG index? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/rag/clear`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('RAG index cleared successfully');
        fetchStats();
      } else {
        toast.error('Failed to clear index');
      }
    } catch (error) {
      toast.error('Failed to clear index');
    }
  };

  const handleUpdateScheduler = async () => {
    try {
      const response = await fetch(`${API_URL}/api/scheduler/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enabled: schedulerEnabled,
          schedule: scheduleType,
          bleu_threshold: bleuThreshold,
          sample_size: sampleSize
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setSchedulerConfig(data.config);
      } else {
        toast.error(data.detail || 'Failed to update scheduler');
      }
    } catch (error) {
      toast.error('Failed to update scheduler');
    }
  };

  const handleRunScheduledNow = async () => {
    try {
      const response = await fetch(`${API_URL}/api/scheduler/run-now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.info('Scheduled benchmark triggered manually');
        // Refresh after a delay
        setTimeout(() => {
          fetchAlerts();
          fetchBenchmarkHistory();
        }, 5000);
      } else {
        toast.error('Failed to trigger benchmark');
      }
    } catch (error) {
      toast.error('Failed to trigger benchmark');
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/api/scheduler/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Alert acknowledged');
        fetchAlerts();
      }
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/rag/search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: searchQuery,
          source_lang: 'en-ca',
          target_lang: 'fr-ca',
          top_k: 5,
          threshold: 0.5
        })
      });

      const data = await response.json();
      if (response.ok) {
        setSearchResults(data.results || []);
        if (data.results?.length === 0) {
          toast.info('No matching translations found');
        }
      } else {
        toast.error(data.detail || 'Search failed');
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleDownloadFile = async (filename) => {
    try {
      const response = await fetch(`${API_URL}/api/finetune/download/${filename}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Download started');
      } else {
        toast.error('Download failed');
      }
    } catch (error) {
      toast.error('Download failed');
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-96">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
            <p className="text-gray-500 mt-2">Admin privileges required</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="rag-admin-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Globe className="w-6 h-6 text-blue-600" />
            <span className="font-bold text-xl tracking-tight text-gray-900">MeteoTranslate</span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/translate" className="text-sm font-medium text-gray-600 hover:text-gray-900">
              Translate
            </Link>
            <Link to="/terminology" className="text-sm font-medium text-gray-600 hover:text-gray-900">
              Terminology
            </Link>
            <Link to="/metrics" className="text-sm font-medium text-gray-600 hover:text-gray-900">
              Metrics
            </Link>
            <Link to="/history" className="text-sm font-medium text-gray-600 hover:text-gray-900">
              History
            </Link>
            <Link to="/settings" className="text-sm font-medium text-gray-600 hover:text-gray-900">
              Settings
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Navigation */}
        <Link 
          to="/translate" 
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6"
          data-testid="back-to-translate"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Translate
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Brain className="w-8 h-8 text-purple-600" />
            RAG & Fine-Tuning Admin
          </h1>
          <p className="mt-2 text-gray-600">
            Manage vector embeddings, semantic search index, and fine-tuning data
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* RAG Index Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-600" />
                RAG Vector Index
              </CardTitle>
              <CardDescription>
                ChromaDB semantic search index status
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {ragStats && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-700">
                        {ragStats.en_to_fr_count?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-blue-600">EN → FR Vectors</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-700">
                        {ragStats.fr_to_en_count?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-purple-600">FR → EN Vectors</div>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Vectors</span>
                      <span className="text-lg font-semibold text-gray-900">
                        {ragStats.total_vectors?.toLocaleString() || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-gray-600">Embedding Model</span>
                      <span className="text-sm font-mono text-gray-700">
                        {ragStats.embedding_model || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-gray-600">Dimensions</span>
                      <span className="text-sm font-mono text-gray-700">
                        {ragStats.embedding_dimensions || 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Dataset Type Breakdown */}
                  {datasetStats && (
                    <div className="p-4 bg-gradient-to-r from-green-50 to-yellow-50 rounded-lg border border-green-100">
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-3">
                        Dataset Breakdown
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-green-700 flex items-center gap-1">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            Training
                          </span>
                          <span className="font-semibold text-green-800">
                            {datasetStats.totals?.training?.toLocaleString() || 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-yellow-700 flex items-center gap-1">
                            <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                            Validation
                          </span>
                          <span className="font-semibold text-yellow-800">
                            {datasetStats.totals?.validation?.toLocaleString() || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <label className="flex-1">
                      <input
                        type="file"
                        accept=".tmx"
                        onChange={handleIndexTMX}
                        className="hidden"
                        disabled={indexing || activeJob}
                      />
                      <Button 
                        variant="outline" 
                        className="w-full"
                        disabled={indexing || activeJob}
                        asChild
                      >
                        <span className="cursor-pointer">
                          {indexing || activeJob ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          {indexing || activeJob ? 'Indexing...' : 'Index TMX File (Bidirectional)'}
                        </span>
                      </Button>
                    </label>
                    <Button 
                      variant="outline" 
                      onClick={() => { fetchStats(); fetchIndexingJobs(); }}
                      size="icon"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={handleClearIndex}
                      size="icon"
                      title="Clear Index"
                      disabled={indexing || activeJob}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Active Job Progress */}
                  {activeJob && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg animate-pulse">
                      <div className="flex items-center gap-2 mb-2">
                        <Activity className="w-4 h-4 text-blue-600 animate-pulse" />
                        <span className="font-medium text-blue-700">Background Indexing Active</span>
                      </div>
                      <div className="text-sm text-blue-600 mb-2">
                        {activeJob.file_name && <span className="font-mono">{activeJob.file_name}</span>}
                      </div>
                      <div className="text-sm text-blue-800 mb-2">
                        {activeJob.message}
                      </div>
                      <div className="flex gap-4 text-xs text-blue-600">
                        {activeJob.status === 'indexing_en_fr' && (
                          <span className="flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            EN→FR: {activeJob.en_fr_indexed?.toLocaleString() || 0}
                          </span>
                        )}
                        {activeJob.status === 'indexing_fr_en' && (
                          <>
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-green-600" />
                              EN→FR: {activeJob.en_fr_indexed?.toLocaleString() || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              FR→EN: {activeJob.fr_en_indexed?.toLocaleString() || 0}
                            </span>
                          </>
                        )}
                        {activeJob.total_pairs > 0 && (
                          <span>Total pairs: {activeJob.total_pairs?.toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recent Jobs */}
                  {indexingJobs.length > 0 && !activeJob && (
                    <div className="border-t pt-4 mt-4">
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Recent Jobs
                      </div>
                      <div className="space-y-2">
                        {indexingJobs.slice(0, 3).map((job) => (
                          <div key={job.job_id} className="flex justify-between items-center text-xs p-2 bg-gray-50 rounded">
                            <span className="font-mono truncate max-w-[150px]">{job.file_name}</span>
                            <span className={`px-2 py-0.5 rounded ${
                              job.status === 'completed' ? 'bg-green-100 text-green-700' :
                              job.status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {job.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Semantic Search Test */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5 text-green-600" />
                Test Semantic Search
              </CardTitle>
              <CardDescription>
                Query the RAG index to find similar translations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Enter English text to search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  rows={2}
                  className="flex-1"
                />
              </div>
              <Button 
                onClick={handleSearch} 
                disabled={searching || !searchQuery.trim()}
                className="w-full"
              >
                {searching ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Search Similar Translations
              </Button>

              {searchResults.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {searchResults.map((result, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg text-sm">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium text-gray-700">Match #{i + 1}</span>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                          {(result.similarity * 100).toFixed(0)}% match
                        </span>
                      </div>
                      <div className="text-gray-600 truncate" title={result.source_text}>
                        <span className="font-medium">EN:</span> {result.source_text?.substring(0, 80)}...
                      </div>
                      <div className="text-blue-600 truncate mt-1" title={result.target_text}>
                        <span className="font-medium">FR:</span> {result.target_text?.substring(0, 80)}...
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fine-Tuning Data */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-600" />
                Fine-Tuning Data Files
              </CardTitle>
              <CardDescription>
                Generate and download OpenAI-format JSONL files for model fine-tuning
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <label className="flex-1">
                  <input
                    type="file"
                    accept=".tmx"
                    onChange={handlePrepareFinetuning}
                    className="hidden"
                    disabled={preparing}
                  />
                  <Button 
                    variant="outline" 
                    className="w-full"
                    disabled={preparing}
                    asChild
                  >
                    <span className="cursor-pointer">
                      {preparing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <FileText className="w-4 h-4 mr-2" />
                      )}
                      {preparing ? 'Preparing...' : 'Prepare Fine-Tuning Data from TMX'}
                    </span>
                  </Button>
                </label>
                <Button 
                  variant="outline" 
                  onClick={fetchFinetuneFiles}
                  size="icon"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              {finetuneFiles.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">File Name</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Size</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Modified</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-600">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {finetuneFiles.slice(0, 10).map((file, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-mono text-xs truncate max-w-xs" title={file.name}>
                            {file.name}
                          </td>
                          <td className="px-4 py-2 text-gray-600">
                            {file.size_mb > 1 ? `${file.size_mb} MB` : `${file.size_bytes} B`}
                          </td>
                          <td className="px-4 py-2 text-gray-600">
                            {new Date(file.modified).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadFile(file.name)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No fine-tuning files generated yet</p>
                  <p className="text-sm">Upload a TMX file to generate training data</p>
                </div>
              )}

              {/* Fine-tuning Instructions */}
              <div className="p-4 bg-yellow-50 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-2">How to use these files:</h4>
                <ol className="text-sm text-yellow-700 space-y-1 list-decimal list-inside">
                  <li>Download the <code className="bg-yellow-100 px-1 rounded">*_train_*.jsonl</code> file</li>
                  <li>Upload to OpenAI: <code className="bg-yellow-100 px-1 rounded">openai api files.create -f file.jsonl -p fine-tune</code></li>
                  <li>Create job: <code className="bg-yellow-100 px-1 rounded">openai api fine_tuning.jobs.create -m gpt-4o-mini -t file-id</code></li>
                  <li>Estimated cost: ~$60 for full dataset with gpt-4o-mini</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Quality Benchmark Card */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-indigo-600" />
                Quality Benchmark
              </CardTitle>
              <CardDescription>
                Test translation quality against validation dataset using BLEU scores
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Benchmark Controls */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Sample Size:</label>
                  <Input
                    type="number"
                    min={10}
                    max={100}
                    value={sampleSize}
                    onChange={(e) => setSampleSize(Math.min(100, Math.max(10, parseInt(e.target.value) || 50)))}
                    className="w-20"
                    disabled={benchmarkRunning}
                  />
                </div>
                <Button 
                  onClick={handleRunBenchmark}
                  disabled={benchmarkRunning || !datasetStats?.totals?.validation}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {benchmarkRunning ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <BarChart3 className="w-4 h-4 mr-2" />
                  )}
                  {benchmarkRunning ? 'Running...' : 'Run Benchmark'}
                </Button>
                <Button
                  variant="outline"
                  onClick={fetchBenchmarkHistory}
                  size="icon"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              {/* Validation data warning */}
              {(!datasetStats?.totals?.validation || datasetStats.totals.validation === 0) && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  No validation data indexed. Upload val_v4.tmx using the Index TMX button above.
                </div>
              )}

              {/* Active Benchmark Progress */}
              {activeBenchmark && activeBenchmark.status === 'running' && (
                <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg animate-pulse">
                  <div className="flex items-center gap-2 mb-2">
                    <Loader2 className="w-4 h-4 text-indigo-600 animate-spin" />
                    <span className="font-medium text-indigo-700">Benchmark Running</span>
                  </div>
                  <p className="text-sm text-indigo-600">
                    Testing {activeBenchmark.total_samples} translations against validation data...
                  </p>
                </div>
              )}

              {/* Completed Benchmark Result */}
              {activeBenchmark && activeBenchmark.status === 'completed' && activeBenchmark.summary && (
                <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-gray-800">Latest Benchmark Results</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(activeBenchmark.completed_at).toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                      <div className="text-2xl font-bold text-indigo-600">
                        {activeBenchmark.summary.avg_bleu_score}
                      </div>
                      <div className="text-xs text-gray-500">Avg BLEU Score</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                      <div className="text-2xl font-bold text-green-600">
                        {activeBenchmark.summary.exact_match_rate}%
                      </div>
                      <div className="text-xs text-gray-500">Exact Match</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                      <div className="text-2xl font-bold text-purple-600">
                        {activeBenchmark.summary.total_samples}
                      </div>
                      <div className="text-xs text-gray-500">Samples Tested</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                      <div className="text-2xl font-bold text-amber-600">
                        {activeBenchmark.summary.avg_processing_time_ms}ms
                      </div>
                      <div className="text-xs text-gray-500">Avg Time</div>
                    </div>
                  </div>

                  <div className="p-3 bg-white rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-indigo-600" />
                      <span className="font-medium text-gray-700">Quality Assessment</span>
                    </div>
                    <p className="text-sm text-gray-600">{activeBenchmark.interpretation}</p>
                  </div>

                  {/* BLEU Distribution */}
                  {activeBenchmark.summary.bleu_distribution && (
                    <div className="mt-4 p-3 bg-white rounded-lg">
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">BLEU Score Distribution</div>
                      <div className="flex gap-1 h-8">
                        {Object.entries(activeBenchmark.summary.bleu_distribution).map(([bucket, count]) => {
                          const total = activeBenchmark.summary.total_samples;
                          const pct = total > 0 ? (count / total) * 100 : 0;
                          const colors = {
                            '0-20': 'bg-red-400',
                            '20-40': 'bg-orange-400',
                            '40-60': 'bg-yellow-400',
                            '60-80': 'bg-green-400',
                            '80-100': 'bg-emerald-500'
                          };
                          return (
                            <div 
                              key={bucket} 
                              className={`${colors[bucket]} rounded flex-1 flex items-end justify-center`}
                              style={{ height: `${Math.max(pct, 5)}%` }}
                              title={`${bucket}: ${count} (${pct.toFixed(1)}%)`}
                            >
                              <span className="text-xs text-white font-medium">{count > 0 ? count : ''}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>0-20</span>
                        <span>20-40</span>
                        <span>40-60</span>
                        <span>60-80</span>
                        <span>80-100</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Benchmark History */}
              {benchmarkHistory.length > 0 && (
                <div className="border-t pt-4">
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Previous Benchmarks
                  </div>
                  <div className="space-y-2">
                    {benchmarkHistory.slice(0, 5).map((b) => (
                      <div key={b.benchmark_id} className="flex justify-between items-center text-xs p-2 bg-gray-50 rounded">
                        <span className="text-gray-600">
                          {new Date(b.started_at).toLocaleDateString()} - {b.total_samples} samples
                        </span>
                        <div className="flex items-center gap-2">
                          {b.avg_bleu_score && (
                            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded font-medium">
                              BLEU: {b.avg_bleu_score}
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded ${
                            b.status === 'completed' ? 'bg-green-100 text-green-700' :
                            b.status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {b.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Scheduled Benchmarks & Alerts Card */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-600" />
                Scheduled Benchmarks & Alerts
              </CardTitle>
              <CardDescription>
                Automatic quality monitoring with threshold alerts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Scheduler Configuration */}
              <div className="p-4 bg-orange-50 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="schedulerEnabled"
                      checked={schedulerEnabled}
                      onChange={(e) => setSchedulerEnabled(e.target.checked)}
                      className="w-4 h-4 text-orange-600 rounded"
                    />
                    <label htmlFor="schedulerEnabled" className="font-medium text-gray-700">
                      Enable Scheduled Benchmarks
                    </label>
                  </div>
                  {schedulerConfig?.next_run && (
                    <span className="text-xs text-orange-600">
                      Next run: {new Date(schedulerConfig.next_run).toLocaleString()}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Schedule</label>
                    <select
                      value={scheduleType}
                      onChange={(e) => setScheduleType(e.target.value)}
                      className="w-full px-3 py-2 border rounded text-sm"
                      disabled={!schedulerEnabled}
                    >
                      <option value="daily">Daily (3 AM UTC)</option>
                      <option value="weekly">Weekly (Mon 3 AM)</option>
                      <option value="hourly">Hourly</option>
                      <option value="every_30_minutes">Every 30 min (testing)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">BLEU Threshold</label>
                    <Input
                      type="number"
                      min={10}
                      max={80}
                      value={bleuThreshold}
                      onChange={(e) => setBleuThreshold(parseInt(e.target.value) || 30)}
                      disabled={!schedulerEnabled}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Sample Size</label>
                    <Input
                      type="number"
                      min={10}
                      max={100}
                      value={sampleSize}
                      onChange={(e) => setSampleSize(parseInt(e.target.value) || 50)}
                      disabled={!schedulerEnabled}
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleUpdateScheduler}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Save Schedule
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleRunScheduledNow}
                    disabled={!datasetStats?.totals?.validation}
                  >
                    Run Now
                  </Button>
                </div>
              </div>

              {/* Alerts */}
              {alerts.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Recent Alerts
                  </div>
                  {alerts.slice(0, 5).map((alert, i) => (
                    <div 
                      key={i} 
                      className={`p-3 rounded-lg flex justify-between items-start ${
                        alert.severity === 'critical' ? 'bg-red-50 border border-red-200' :
                        alert.severity === 'error' ? 'bg-red-50 border border-red-200' :
                        'bg-amber-50 border border-amber-200'
                      }`}
                    >
                      <div>
                        <div className={`text-sm font-medium ${
                          alert.severity === 'critical' || alert.severity === 'error' ? 'text-red-700' : 'text-amber-700'
                        }`}>
                          {alert.type === 'quality_drop' ? '⚠️ Quality Drop' : '❌ Error'}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">{alert.message}</div>
                        <div className="text-xs text-gray-400 mt-1">
                          {new Date(alert.created_at).toLocaleString()}
                        </div>
                      </div>
                      {!alert.acknowledged && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleAcknowledgeAlert(alert.benchmark_id)}
                          className="text-xs"
                        >
                          Acknowledge
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {alerts.length === 0 && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500 opacity-50" />
                  No alerts - quality is within threshold
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
